import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { StoreHandoffService } from 'src/app/services/store-handoff.service';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { FormHelpers } from 'src/app/common/formHelpers';
import { AdminUIHelpers } from 'src/app/common/helpers';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { IPlannedHandoffException, UpsertPlannedHandoffException } from 'src/app/common/models/plannedHandoffException';
import { StatusService } from 'src/app/services/status.service';
import { catchError, from, map, mergeMap, Observable, of, tap, toArray } from 'rxjs';
import { PlannedHandoffExceptionDateValidator } from './schedule-planned-handoff-exception.validator.directive';
import { conditionallyLog } from 'src/app/common/consoleLogHelper';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatStepper, MatStep, MatStepLabel, MatStepperNext, MatStepperPrevious } from '@angular/material/stepper';

import { MatFormField, MatLabel, MatInput, MatSuffix } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MultiStoreDropdown } from '../../multi-store-dropdown/multi-store-dropdown.component';

interface IPlannedHandoffResult {
  facilityId: string;
  isError: boolean;
  error?: any;
  plannedHandoffException?: IPlannedHandoffException;
}

export interface ISchedulePlannedHandoffExceptionInput {
  handoffType: string;
  exceptionToEdit?: IPlannedHandoffException;
}

@Component({
  selector: 'app-schedule-planned-handoff-exception',
  templateUrl: './schedule-planned-handoff-exception.component.html',
  styleUrls: ['./schedule-planned-handoff-exception.component.scss'],
  standalone: true,
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatStepper,
    MatStep,
    FormsModule,
    ReactiveFormsModule,
    MatStepLabel,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatCheckbox,
    MatButton,
    MatStepperNext,
    MultiStoreDropdown,
    MatStepperPrevious,
  ],
})
export class SchedulePlannedHandoffExceptionComponent implements OnInit {
  public isEditMode = false;
  public plannedExceptionForm!: UntypedFormGroup;
  public multiStoreForm!: UntypedFormGroup;

  public handoffType: string = this.data.handoffType;
  public facilities: IMultiStoreSelect[] = [];
  public currentFacilityId: string;

  constructor(
    public dialogRef: MatDialogRef<SchedulePlannedHandoffExceptionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ISchedulePlannedHandoffExceptionInput,
    private readonly authManager: AuthManager,
    private readonly storeHandoffService: StoreHandoffService,
    private readonly facilityManager: FacilityManager,
    private readonly toastr: ToastrService,
    private readonly fb: UntypedFormBuilder,
    public readonly statusService: StatusService,
  ) {
    this.currentFacilityId = this.facilityManager.getCurrentFacility();
  }

  public ngOnInit(): void {
    this.plannedExceptionForm = this.fb.group(
      {
        startOn: [this.startOfToday(), Validators.required],
        endOn: [this.endOfDay(this.futureDate(1)), Validators.required],
        showWarningOn: [this.startOfToday(), Validators.required],
        isMultiStore: false,
      },
      {
        validators: PlannedHandoffExceptionDateValidator(),
      },
    );

    this.multiStoreForm = this.fb.group({
      facilities: ['', Validators.required],
    });

    if (this.data.exceptionToEdit) {
      this.isEditMode = true;

      this.plannedExceptionForm.patchValue({
        startOn: this.data.exceptionToEdit.startOn,
        endOn: this.data.exceptionToEdit.endOn,
        showWarningOn: this.data.exceptionToEdit.showWarningOn,
        isMultiStore: false,
      });
    }
  }

  public confirmationString(): string {
    if (this.isEditMode) {
      return 'Are you sure you want to update this planned outage for this facility?';
    } else {
      const selectedFacilityCount = this.selectedFacilityCount();
      if (selectedFacilityCount > 1) {
        return `Are you sure you want to create a planned outage for ${selectedFacilityCount} facilities?`;
      } else if (selectedFacilityCount === 1) {
        return 'Are you sure you want to create a planned outage for this facility?';
      } else {
        return 'No facilities selected.';
      }
    }
  }

  public selectedFacilityCount(): number {
    return AdminUIHelpers.getSelectedFacilityIds(this.currentFacilityId, this.facilities).length;
  }

  public canApplyMultiStore(): boolean {
    if (this.isEditMode) {
      return false;
    } else {
      return this.authManager.isCurrentAccountAdminOrITRole();
    }
  }

  public facilityValidation(): void {
    this.multiStoreForm.get('facilities')?.setValue(FormHelpers.areOptionsChecked(this.facilities));
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public saveAction(): void {
    if (this.isEditMode) {
      this.editExistingPlannedHandoffException();
    } else {
      this.createNewPlannedHandoffExceptions();
    }
  }

  public createNewPlannedHandoffExceptions(): void {
    const exception: UpsertPlannedHandoffException = {
      handoffType: this.data.handoffType,
      startOn: this.plannedExceptionForm.value.startOn,
      endOn: this.plannedExceptionForm.value.endOn,
      showWarningOn: this.plannedExceptionForm.value.showWarningOn,
    };

    exception.endOn.setHours(23, 59, 59, 999);

    const isMultiStore = this.plannedExceptionForm.value.isMultiStore;
    if (isMultiStore) {
      this.handleMultiStoreSave(exception);
    } else {
      this.handleSingleStoreSave(exception);
    }
  }

  public editExistingPlannedHandoffException(): void {
    conditionallyLog('Editing planned handoff exception', this.data.exceptionToEdit);
    if (this.data.exceptionToEdit?.exceptionId === undefined) {
      console.error('Unable to edit planned handoff exception: Exception ID is undefined');
      this.toastr.error('Failed to edit planned hand-off outage.');
      return;
    }
    const exception: UpsertPlannedHandoffException = {
      handoffType: this.data.handoffType,
      startOn: new Date(this.plannedExceptionForm.value.startOn),
      endOn: new Date(this.plannedExceptionForm.value.endOn),
      showWarningOn: new Date(this.plannedExceptionForm.value.showWarningOn),
      exceptionId: this.data.exceptionToEdit.exceptionId,
    };

    exception.endOn.setHours(23, 59, 59, 999);

    this.handleSingleStoreSave(exception);
  }

  private handleMultiStoreSave(exception: UpsertPlannedHandoffException): void {
    const facilityIds = AdminUIHelpers.getSelectedFacilityIds(this.currentFacilityId, this.facilities);
    const observables: Observable<IPlannedHandoffResult>[] = this.buildEnabledObservableCollection(
      exception,
      facilityIds,
    );
    this.statusService.batchProgressSetup(observables.length);

    from(observables)
      .pipe(
        mergeMap((obs) => obs, 10), // run 10 at a time
        toArray(), // collect all results into an array so we can process them all at once later
      )
      .subscribe({
        next: (results) => {
          const success = this.handleBatchResults(results);
          if (success) {
            const currentFacilityResult = results.find((r) => r.facilityId === this.currentFacilityId && !r.isError);
            if (!currentFacilityResult) {
              this.toastr.error('An error occurred while creating planned hand-off outage for current facility');
            } else {
              this.dialogRef.close(currentFacilityResult);
            }
          }
        },
        error: () => {
          this.toastr.error('An error occurred while creating planned hand-off outage for current facility');
        },
      });
  }

  private buildEnabledObservableCollection(
    upsertPlannedHandoffException: UpsertPlannedHandoffException,
    facilities: string[],
  ) {
    const saveObservables: Observable<IPlannedHandoffResult>[] = [];
    facilities.forEach((facilityId) => {
      saveObservables.push(
        this.storeHandoffService
          .upsertPlannedHandoffHandoffException(facilityId, upsertPlannedHandoffException)
          .pipe(
            map(
              (response) =>
                ({
                  facilityId: facilityId,
                  isError: false,
                  plannedHandoffException: response,
                }) as IPlannedHandoffResult,
            ),
            catchError((err): Observable<IPlannedHandoffResult> => {
              const errObj = of({
                facilityId: facilityId,
                isError: true,
                err,
              } as IPlannedHandoffResult);
              return errObj;
            }),
          )
          .pipe(
            tap(() => {
              this.statusService.incrementCompletedRequests();
            }),
          ),
      );
    });
    return saveObservables;
  }

  private handleSingleStoreSave(exception: UpsertPlannedHandoffException): void {
    this.storeHandoffService.upsertPlannedHandoffHandoffException(this.currentFacilityId, exception).subscribe({
      next: (result) => {
        if (this.isEditMode) {
          this.toastr.success('Successfully updated planned hand-off outage');
        }
        this.toastr.success('Successfully scheduled planned handoff outage');
        this.dialogRef.close(result);
      },
      error: () => {
        this.toastr.error('Error while scheduling planned handoff outage');
      },
    });
  }

  private handleBatchResults(results: any[]): boolean {
    const successes = results.filter((r) => !r.isError);
    const failures = results.filter((r) => r.isError);

    if (failures.length === 0) {
      this.toastr.success(`Successfully scheduled planned hand-off outages at ${results.length} facilities`);
    } else if (successes.length > 0) {
      const failedFacilities = failures.map((e) => `Facility ${e.facilityId}`).join(', ');
      this.toastr.warning(
        `Scheduled Planned Hand-off outages at ${successes.length} facilities, but ${failures.length} failed: ${failedFacilities}`,
        'Partial Failure',
        { timeOut: 10000 },
      );
    } else {
      this.toastr.error('An error occurred while creating planned handoff outage');
      return false;
    }

    return true;
  }

  private futureDate(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  private endOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  private startOfToday(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }
}
